"use client";

import type * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Tabs as TabsPrimitive } from "@base-ui/react/tabs";

import { cn } from "@/lib/utils";

function Tabs({
  className,
  orientation = "horizontal",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      className={cn(
        "group/tabs flex gap-2",
        orientation === "horizontal" ? "flex-col" : "flex-row",
        className,
      )}
      {...props}
    />
  );
}

const tabsListVariants = cva(
  "group/tabs-list inline-flex w-fit items-center justify-center rounded-lg p-[3px] text-muted-foreground data-[orientation=vertical]:h-fit data-[orientation=vertical]:flex-col data-[variant=line]:rounded-none",
  {
    variants: {
      variant: {
        default: "bg-muted data-[orientation=horizontal]:h-8",
        line: "gap-1 bg-transparent data-[orientation=horizontal]:h-8",
        lifted:
          "w-full items-end justify-start gap-1 overflow-x-auto overflow-y-hidden rounded-none bg-muted/30 px-2 pt-1 pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden data-[orientation=horizontal]:h-10",
        navigation:
          "gap-1 rounded-none bg-transparent p-0 data-[orientation=horizontal]:h-10",
        // Spreadsheet sheet tabs: a bar above the grid, tabs shoulder to
        // shoulder, the open sheet lifted onto the surface with an accent rule.
        sheet:
          "w-full items-stretch justify-start gap-0 overflow-x-auto rounded-none border-b border-border/70 bg-muted p-0 scrollbar-thin data-[orientation=horizontal]:h-8",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function TabsList({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Tab>) {
  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(
        "relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-1.5 py-0.5 text-sm font-medium whitespace-nowrap text-tertiary-foreground transition-colors group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 group-data-[variant=default]/tabs-list:data-active:shadow-sm group-data-[variant=line]/tabs-list:data-active:shadow-none",
        "group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-active:bg-transparent group-data-[variant=navigation]/tabs-list:h-6.5 group-data-[variant=navigation]/tabs-list:flex-none group-data-[variant=navigation]/tabs-list:px-2 group-data-[variant=navigation]/tabs-list:text-xs group-data-[variant=navigation]/tabs-list:text-muted-foreground",
        "group-data-[variant=lifted]/tabs-list:after:hidden group-data-[variant=lifted]/tabs-list:h-9 group-data-[variant=lifted]/tabs-list:flex-none group-data-[variant=lifted]/tabs-list:rounded-t-md group-data-[variant=lifted]/tabs-list:rounded-b-none group-data-[variant=lifted]/tabs-list:bg-transparent group-data-[variant=lifted]/tabs-list:px-3 group-data-[variant=lifted]/tabs-list:text-base group-data-[variant=lifted]/tabs-list:font-normal group-data-[variant=lifted]/tabs-list:shadow-none group-data-[variant=lifted]/tabs-list:data-active:z-10 group-data-[variant=lifted]/tabs-list:data-active:border-border/60 group-data-[variant=lifted]/tabs-list:data-active:border-b-background group-data-[variant=lifted]/tabs-list:data-active:bg-background group-data-[variant=lifted]/tabs-list:data-active:font-medium group-data-[variant=lifted]/tabs-list:data-active:shadow-none",
        "data-active:bg-background data-active:text-foreground group-data-[variant=navigation]/tabs-list:data-active:shadow-sm group-data-[variant=navigation]/tabs-list:data-active:ring-1 group-data-[variant=navigation]/tabs-list:data-active:ring-border/70",
        "group-data-[variant=sheet]/tabs-list:h-full group-data-[variant=sheet]/tabs-list:flex-none group-data-[variant=sheet]/tabs-list:rounded-none group-data-[variant=sheet]/tabs-list:border-0 group-data-[variant=sheet]/tabs-list:border-r group-data-[variant=sheet]/tabs-list:border-b-2 group-data-[variant=sheet]/tabs-list:border-r-border/70 group-data-[variant=sheet]/tabs-list:border-b-transparent group-data-[variant=sheet]/tabs-list:px-3 group-data-[variant=sheet]/tabs-list:py-0 group-data-[variant=sheet]/tabs-list:text-xs group-data-[variant=sheet]/tabs-list:font-normal group-data-[variant=sheet]/tabs-list:text-muted-foreground group-data-[variant=sheet]/tabs-list:data-active:border-b-primary group-data-[variant=sheet]/tabs-list:data-active:bg-surface group-data-[variant=sheet]/tabs-list:data-active:font-semibold group-data-[variant=sheet]/tabs-list:data-active:text-primary",
        "after:absolute after:bg-foreground after:opacity-0 after:transition-opacity group-data-[orientation=horizontal]/tabs:after:inset-x-0 group-data-[orientation=horizontal]/tabs:after:bottom-[-5px] group-data-[orientation=horizontal]/tabs:after:h-0.5 group-data-[orientation=vertical]/tabs:after:inset-y-0 group-data-[orientation=vertical]/tabs:after:-right-1 group-data-[orientation=vertical]/tabs:after:w-0.5 group-data-[variant=line]/tabs-list:data-active:after:opacity-100",
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Panel>) {
  return (
    <TabsPrimitive.Panel
      data-slot="tabs-content"
      className={cn("flex-1 text-sm outline-none", className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants };
